import { FlatButton, Dialog, IconButton, H2, Dropdown, Description, Icon } from "@evergis/ui";
import styled from "styled-components";
import srcChart from "./assets/chart.svg";

export enum AxisContainerSize {
  small,
  medium,
  large,
}

const getAxisContainerSize = (size: AxisContainerSize): string => {
  switch (size) {
    case AxisContainerSize.small: {
      return "3.5rem";
    }
    case AxisContainerSize.medium: {
      return "4rem";
    }
    case AxisContainerSize.large: {
      return "5.625rem";
    }
    default: {
      return "3.5rem";
    }
  }
};

export const DateFilterContainer = styled.div<{ size: AxisContainerSize }>`
  position: relative;
  margin-top: 1rem;
  width: 100%;
  height: ${({ size }) => getAxisContainerSize(size)};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
  background-color: ${({ theme: { palette } }) => palette.panelBackground};
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  box-shadow: ${({ theme: { shadows } }) => shadows.raised};
  overflow: hidden;
`;

export const RangeSelectorContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`;

export const StubChart = styled.div`
  flex: 1;
  height: 100%;
  background: url(${srcChart});
  background-repeat: repeat-x;
  background-position: center;
`;

export const LayerButton = styled(FlatButton)`
  background-color: ${({ theme: { palette } }) => palette.elementDark};
  margin: 0 1rem;
  height: 1.25rem;
  min-height: 1.25rem;
  max-width: 30rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  border-radius: 0;
  font-size: 0.625rem;
  letter-spacing: 0.006rem;
  text-transform: none;

  :hover {
    background-color: ${({ theme: { palette } }) => palette.elementDeep};
  }
`;

export const SettingsIcon = styled(Icon)`
  width: 0.75rem;
  height: 0.75em;

  &:after {
    font-size: 0.75rem;
  }
`;

export const ButtonText = styled.span`
  margin-left: 0.5rem;
`;

export const AxisControllerContainer = styled.div`
  height: 100%;
  align-items: center;
  position: relative;
  display: flex;
`;

const AxisControllerGradient = styled.div`
  pointer-events: none;
  position: absolute;
  width: 1.5rem;
  height: 100%;
`;

export const GradientLeft = styled(AxisControllerGradient)`
  background: linear-gradient(270deg, rgba(25, 32, 72, 0) 0%, rgba(25, 32, 72, 0.05) 100%, rgba(25, 32, 72, 0.05) 100%);
  right: -1.5rem;
`;

export const GradientRight = styled(AxisControllerGradient)`
  background: linear-gradient(270deg, rgba(25, 32, 72, 0.05) 0%, rgba(25, 32, 72, 0) 100%);
  left: -1.5rem;
`;

export const Space = styled.div`
  width: 1rem;
`;

export const AxisContainer = styled.div`
  flex: 1;
  height: 100%;
  overflow-x: scroll;
  overflow-y: hidden;
`;

export const ModalContainer = styled(Dialog)`
  display: flex;
`;

export const ModalHeader = styled.div`
  padding: 3rem;
  padding-bottom: 1.5rem;
  position: relative;
  z-index: 10;
`;

export const Title = styled(H2)``;
export const Closeicon = styled(IconButton)`
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
`;

export const TimeSettings = styled.div`
  display: flex;
`;

export const TimeSettingsItem = styled.div`
  margin-right: 1rem;
`;

export const SmallFlatButton = styled(FlatButton)`
  white-space: nowrap;
  max-width: none;
  min-height: 0;
  font-size: 0.75rem;
  text-transform: none;
  padding: 0.1rem;
  margin-top: 0.25rem;
`;

export const FastStepGroupButtons = styled(Description)`
  margin: 0;
`;

export const ModalBottom = styled.div`
  padding: 3rem;
  display: flex;
  justify-content: space-between;
`;

export const Buttons = styled.div``;

export const CancelButton = styled(FlatButton)`
  margin-right: 1rem;
`;

export const Card = styled.div`
  width: 100%;
  display: grid;
  /* grid-template-columns: [left][checkbox][layer][icons][right] */
  background-color: ${({ theme: { palette } }) => palette.background};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
  padding: 0.75rem 1.5rem;
  box-sizing: border-box;
  margin-top: 1.5rem;
`;

export const Line = styled.div`
  display: flex;
  align-items: center;
`;

export const SecondLineLayout = styled.div`
  margin-left: 2.75rem;
  display: flex;
  align-items: end;
`;

export const SecondSelectLayerContainer = styled.div`
  width: 30rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const ColorPickerContainer = styled.div`
  margin-left: 1rem;
`;

const SelectContainer = styled.div`
  margin: 0 0 1rem 1rem;
  position: relative;
`;

export const SelectLayerContainer = styled(SelectContainer)`
  flex: 2;
`;

export const SelectAttributeContainer = styled(SelectContainer)`
  flex: 1;
`;

export const Select = styled(Dropdown)``;

export const CalculationContainer = styled.div`
  display: flex;
  position: absolute;
  align-items: center;
  padding: 0.25rem;
  width: 100%;
`;

export const CalculateDescription = styled.div`
  color: ${({ theme: { palette } }) => palette.textSecondary};
  font-size: 0.625rem;
  white-space: nowrap;
  padding-right: 0.5rem;
`;

// TODO REFACTORING move to IconButton
export const UploadDateButton = styled(IconButton)`
  display: flex;
  flex-direction: row-reverse;
  align-items: flex-start;
  height: 0.75rem;
  font-size: 0.625rem;
  > span {
    font-size: 0.625rem;
    margin: 0.25rem;
    :after {
      font-size: 0.75rem !important;
    }
  }
`;

const AxisLabel = styled.text`
  font-family: "Roboto", sans-serif;
  font-style: normal;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: 0.1px;
  fill: ${({ theme: { palette } }) => palette.textPrimary};
  mix-blend-mode: normal;
  user-select: none;
`;

export const TickLabel = styled(AxisLabel)`
  font-weight: 400;

  opacity: 0.5;
`;

export const Tick = styled.rect`
  transition: height 0.15s ease, transform 0.15s ease, fill 0.15s ease, opacity 0.15s ease;
  transform-origin: center center;
  user-select: none;
`;

export const TooltipLabel = styled(AxisLabel)`
  transition: transform 0.15s ease;
  font-weight: 500;
`;
